/**
 * NutritionScreen.jsx
 * Full nutrition tracking with real backend integration
 * Features: Food search, Barcode scanner, AI photo analysis, meal logging, Voice input
 * Updated: Day navigation, Micronutrient display, Voice Food Input (like Kundenportal)
 */
import React, { useState, useEffect, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import { useTranslation } from 'react-i18next';
import { 
  BookOpen, ShoppingCart, Utensils, Scale, Plus, Camera, Search, 
  FileText, Sparkles, RefreshCw, ChevronRight, Droplet, ChevronLeft,
  Pill, Zap, Settings, Copy, Clock, Calendar, Mic, MicOff, Loader2, 
  Check, X, UtensilsCrossed, Image, TrendingUp, Trash2, Edit2, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Import modals
import FoodSearchModal from './FoodSearchModal';
import BarcodeScanner from './BarcodeScanner';
import MealPhotoAnalyzer from './MealPhotoAnalyzer';
import NutritionSettingsModal from './NutritionSettingsModal';
import MealCalculatorModal from './MealCalculatorModal';
import MealPlannerScreen from './MealPlannerScreen';
import NutritionOnboarding from '../../../nutrition/NutritionOnboarding';
import CreateMealModal from './CreateMealModal';
import CreateProductWizard from './CreateProductWizard';
import MobileDayAnalysisChat from './MobileDayAnalysisChat';
import MobileTrainingTab from './MobileTrainingTab';
import MobileAnalyticsTab from './MobileAnalyticsTab';
import MobileRecoveryTab from './MobileRecoveryTab';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Format food display name with brand (filter out "null", "Basis", empty)
const formatFoodName = (entry) => {
  let name = entry.food_name || entry.name || '';
  // Strip leading "null " from food_name (data fix)
  if (name.startsWith('null ')) name = name.substring(5);
  
  const brand = entry.brand;
  if (brand && brand !== 'null' && brand !== 'Basis' && brand !== 'AI' && brand !== 'Custom' && brand.trim() !== '') {
    // Don't duplicate if name already starts with brand
    if (!name.toLowerCase().startsWith(brand.toLowerCase())) {
      return `${brand} ${name}`;
    }
  }
  return name;
};

// Format date for display
const formatDateDisplay = (dateStr) => {
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDate = new Date(date);
  targetDate.setHours(0, 0, 0, 0);
  
  const diffDays = Math.round((targetDate - today) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Heute';
  if (diffDays === -1) return 'Gestern';
  if (diffDays === 1) return 'Morgen';
  
  const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  return `${days[date.getDay()]}, ${date.getDate()}.${date.getMonth() + 1}.`;
};

/* ─── Swipeable Entry for Mobile ─── */
const SwipeableEntry = ({ entry, editingEntry, editServingSize, setEditServingSize, setEditingEntry, handleUpdateEntry, handleDeleteEntry }) => {
  const [swipeX, setSwipeX] = useState(0);
  const [swiped, setSwiped] = useState(false);

  const handlers = useSwipeable({
    onSwiping: ({ deltaX }) => {
      if (deltaX < 0) setSwipeX(Math.max(deltaX * 0.6, -80));
      else if (swiped) setSwipeX(Math.min(0, -80 + deltaX * 0.6));
    },
    onSwipedLeft: () => { setSwipeX(-80); setSwiped(true); },
    onSwipedRight: () => { setSwipeX(0); setSwiped(false); },
    onTouchEndOrOnMouseUp: () => {
      if (swipeX > -40) { setSwipeX(0); setSwiped(false); }
      else { setSwipeX(-80); setSwiped(true); }
    },
    trackMouse: false,
    trackTouch: true,
    delta: 10,
    preventScrollOnSwipe: true,
  });

  return (
    <div className="relative overflow-hidden rounded-lg" data-testid={`meal-entry-${entry.id}`}>
      {/* Red delete button behind */}
      <div
        className="absolute inset-y-0 right-0 w-20 flex items-center justify-center bg-red-500 rounded-r-lg cursor-pointer active:bg-red-600"
        onClick={() => { handleDeleteEntry(entry.id); setSwipeX(0); setSwiped(false); }}
        data-testid={`swipe-delete-${entry.id}`}
      >
        <Trash2 className="w-5 h-5 text-white" />
      </div>

      {/* Sliding content */}
      <div
        {...handlers}
        className="relative flex items-center justify-between bg-zinc-800/60 rounded-lg px-2 py-1.5"
        style={{
          transform: `translateX(${swipeX}px)`,
          transition: swipeX === 0 || swipeX === -80 ? 'transform 0.2s ease-out' : 'none',
        }}
      >
        {editingEntry === entry.id ? (
          <div className="flex items-center gap-2 w-full">
            <input
              type="number"
              value={editServingSize}
              onChange={(e) => setEditServingSize(e.target.value)}
              className="w-16 bg-zinc-700 border border-zinc-600 rounded px-1.5 py-0.5 text-white text-xs"
              placeholder="g"
              autoFocus
              data-testid={`edit-serving-input-${entry.id}`}
            />
            <span className="text-zinc-400 text-[10px]">g</span>
            <button onClick={() => handleUpdateEntry(entry)} className="p-1 text-green-400 hover:text-green-300" data-testid={`edit-save-${entry.id}`}>
              <Check className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => { setEditingEntry(null); setEditServingSize(''); }} className="p-1 text-zinc-400 hover:text-zinc-300" data-testid={`edit-cancel-${entry.id}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs truncate">{formatFoodName(entry)}</p>
              <p className="text-zinc-500 text-[10px]">
                {entry.serving_size}g · {Math.round(entry.calories || 0)} kcal
                {entry.protein > 0 && ` · ${Math.round(entry.protein)}g P`}
              </p>
            </div>
            {!swiped && (
              <div className="flex items-center gap-0.5">
                <button
                  onClick={() => { setEditingEntry(entry.id); setEditServingSize(String(entry.serving_size || 100)); }}
                  className="p-1 text-zinc-500 hover:text-blue-400 transition-colors"
                  data-testid={`entry-edit-btn-${entry.id}`}
                >
                  <Edit2 className="w-3 h-3" />
                </button>
                <button
                  onClick={() => handleDeleteEntry(entry.id)}
                  className="p-1 text-zinc-500 hover:text-red-400 transition-colors"
                  data-testid={`entry-delete-btn-${entry.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const NutritionScreen = ({ onNavigate, portalToken, customerId }) => {
  const { t } = useTranslation();
  // Sub-tab state (Essen, Training, Analytics, Erholung)
  const [activeSubTab, setActiveSubTab] = useState('food');
  
  // Date state for navigation
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  // State
  const [loading, setLoading] = useState(true);
  const [nutritionData, setNutritionData] = useState(null);
  const [waterIntake, setWaterIntake] = useState(0);
  const [waterGoal, setWaterGoal] = useState(2500);
  const [showMicronutrients, setShowMicronutrients] = useState(false);
  const [showMealDistribution, setShowMealDistribution] = useState(false);
  const [showNutritionLog, setShowNutritionLog] = useState(false);  // Ernährungs-Log collapsed by default
  const [showWaterSection, setShowWaterSection] = useState(false);  // Water section collapsed by default
  const [showTrainingSection, setShowTrainingSection] = useState(false);  // Training section collapsed by default
  const [showMacroSection, setShowMacroSection] = useState(true);  // Tagesanzeige expanded by default
  const [showShoppingList, setShowShoppingList] = useState(false);  // Einkaufsliste collapsed by default
  const [mealDistribution, setMealDistribution] = useState(null);
  const [showMealDistributionEdit, setShowMealDistributionEdit] = useState(false);
  const [editMealForm, setEditMealForm] = useState({ breakfast: 30, lunch: 35, dinner: 25, snacks: 10 });
  const [savingMealDist, setSavingMealDist] = useState(false);
  
  // Modal states
  const [showFoodSearch, setShowFoodSearch] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [showPhotoAnalyzer, setShowPhotoAnalyzer] = useState(false);
  const [showMealCalculator, setShowMealCalculator] = useState(false);  // KI-Kalkulator
  const [showAddMealMenu, setShowAddMealMenu] = useState(false);  // Menu to choose input method
  const [showMealPlanner, setShowMealPlanner] = useState(false);  // Full meal planner
  const [showVoiceInput, setShowVoiceInput] = useState(false);  // NEW: Voice input like Kundenportal
  const [showCreateMeal, setShowCreateMeal] = useState(false);  // NEW: Create custom meal
  const [showCreateProduct, setShowCreateProduct] = useState(false); // NEW: Create custom product wizard
  const [createProductBarcode, setCreateProductBarcode] = useState('');
  
  // Expanded meal sections & editing
  const [expandedMeals, setExpandedMeals] = useState({});
  const [editingEntry, setEditingEntry] = useState(null);
  const [editServingSize, setEditServingSize] = useState('');
  const [showMultiPhoto, setShowMultiPhoto] = useState(false);  // NEW: Multi photo capture
  const [selectedMealType, setSelectedMealType] = useState(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 10) return 'breakfast';
    if (hour >= 10 && hour < 12) return 'snack_morning';
    if (hour >= 12 && hour < 14) return 'lunch';
    if (hour >= 14 && hour < 17) return 'snack_afternoon';
    if (hour >= 17 && hour < 21) return 'dinner';
    return 'snack_evening';
  });
  const [showNutritionSettings, setShowNutritionSettings] = useState(false);
  const [showNutritionOnboarding, setShowNutritionOnboarding] = useState(false);  // Onboarding modal
  const [showDayAnalysis, setShowDayAnalysis] = useState(false);  // KI-Tagesanalyse
  
  // Voice Input States (NEW - like Kundenportal)
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  const [voiceTranscription, setVoiceTranscription] = useState('');
  const [voiceDetectedFoods, setVoiceDetectedFoods] = useState([]);
  const [voiceTotals, setVoiceTotals] = useState({ calories: 0, protein: 0, carbs: 0, fat: 0 });
  const [voiceStep, setVoiceStep] = useState('record'); // 'record', 'confirm', 'meal-select'
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  
  // Yesterday data for "Wie gestern" feature
  const [yesterdayMeals, setYesterdayMeals] = useState({});
  const [copyingMeal, setCopyingMeal] = useState({});
  const [copyConfirm, setCopyConfirm] = useState({
    open: false,
    mealType: null,
    mealLabel: '',
    count: 0
  });
  const [tooltipVisible, setTooltipVisible] = useState({});
  
  // Training Log & Afterburn states
  const [trainingLog, setTrainingLog] = useState(null);
  const [afterburnCalories, setAfterburnCalories] = useState(0);
  const [showTrainingLog, setShowTrainingLog] = useState(false);
  const [activityVariant, setActivityVariant] = useState('weekly');
  
  // Date navigation
  const goToPreviousDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };
  
  const goToNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };
  
  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  // ============== VOICE INPUT FUNCTIONS (like Kundenportal) ==============
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        processVoiceRecording(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Mikrofon-Zugriff verweigert');
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };
  
  const processVoiceRecording = async (audioBlob) => {
    setIsProcessingVoice(true);
    setVoiceStep('confirm');
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      // Step 1: Transcribe
      const transcribeResponse = await axios.post(
        `${API}/nutrition/voice/transcribe`,
        formData,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      
      const transcription = transcribeResponse.data.transcription;
      setVoiceTranscription(transcription);
      
      // Step 2: Parse food
      const parseResponse = await axios.post(
        `${API}/nutrition/voice/parse`,
        { text: transcription },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      
      const foods = parseResponse.data.foods || [];
      setVoiceDetectedFoods(foods);
      
      // Calculate totals
      const totals = foods.reduce((acc, food) => ({
        calories: acc.calories + (food.calories || 0),
        protein: acc.protein + (food.protein || 0),
        carbs: acc.carbs + (food.carbs || 0),
        fat: acc.fat + (food.fat || 0)
      }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
      setVoiceTotals(totals);
      
    } catch (error) {
      console.error('Error processing voice:', error);
      toast.error('Fehler bei der Sprachverarbeitung');
      setVoiceStep('record');
    } finally {
      setIsProcessingVoice(false);
    }
  };
  
  const confirmVoiceEntry = async () => {
    try {
      for (const food of voiceDetectedFoods) {
        await axios.post(
          `${API}/portal/nutrition/entries`,
          {
            date: selectedDate,
            meal_type: selectedMealType,
            food_name: food.name,
            portion_grams: food.portion_grams || 100,
            calories: food.calories || 0,
            protein: food.protein || 0,
            carbs: food.carbs || 0,
            fat: food.fat || 0
          },
          { headers: { Authorization: `Bearer ${portalToken}` } }
        );
      }
      toast.success(`${voiceDetectedFoods.length} Einträge hinzugefügt!`);
      loadNutritionData();
      resetVoiceInput();
    } catch (error) {
      console.error('Error saving voice entry:', error);
      toast.error('Fehler beim Speichern');
    }
  };
  
  const resetVoiceInput = () => {
    setShowVoiceInput(false);
    setVoiceStep('record');
    setVoiceTranscription('');
    setVoiceDetectedFoods([]);
    setVoiceTotals({ calories: 0, protein: 0, carbs: 0, fat: 0 });
    setRecordingTime(0);
  };
  
  // Cleanup voice recording on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  // Load yesterday's meals for "Wie gestern" feature
  const loadYesterdayMeals = async () => {
    if (!portalToken) return;
    try {
      const yesterday = new Date(selectedDate);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/portal/nutrition/summary`, {
        params: { date: yesterdayStr },
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setYesterdayMeals(response.data?.meals || {});
    } catch (error) {
      console.error('Error loading yesterday meals:', error);
    }
  };

  // Copy yesterday's meal to today
  const copyYesterdayMeal = async (mealType) => {
    if (!yesterdayMeals[mealType] || yesterdayMeals[mealType].length === 0) {
      toast.error('Keine Einträge vom Vortag');
      return;
    }

    setCopyingMeal(prev => ({ ...prev, [mealType]: true }));
    try {
      const targetDate = selectedDate;
      const sourceDateObj = new Date(selectedDate);
      sourceDateObj.setDate(sourceDateObj.getDate() - 1);
      const sourceDate = sourceDateObj.toISOString().split('T')[0];

      const response = await axios.post(
        `${API}/portal/nutrition/copy-yesterday/${mealType}`,
        null,
        {
          params: { target_date: targetDate, source_date: sourceDate },
          headers: { Authorization: `Bearer ${portalToken}` }
        }
      );

      toast.success(`${response.data?.count || 0} Einträge übernommen`);
      loadNutritionData();
      loadYesterdayMeals();
    } catch (error) {
      toast.error('Fehler beim Kopieren');
    } finally {
      setCopyingMeal(prev => ({ ...prev, [mealType]: false }));
    }
  };

  const requestCopyYesterday = (mealType, hasMeals) => {
    const label = mealTypes.find(m => m.key === mealType)?.label || mealType;
    if (hasMeals) {
      setCopyConfirm({
        open: true,
        mealType,
        mealLabel: label,
        count: yesterdayMeals[mealType]?.length || 0
      });
      return;
    }
    copyYesterdayMeal(mealType);
  };

  const confirmCopyYesterday = () => {
    if (!copyConfirm.mealType) return;
    copyYesterdayMeal(copyConfirm.mealType);
    setCopyConfirm({ open: false, mealType: null, mealLabel: '', count: 0 });
  };

  const closeCopyConfirm = () => {
    setCopyConfirm({ open: false, mealType: null, mealLabel: '', count: 0 });
  };

  // Load nutrition data
  useEffect(() => {
    if (portalToken) {
      loadNutritionData();
      loadMealDistribution();
      loadYesterdayMeals();
      loadTrainingLog();
    }
  }, [portalToken, selectedDate]);

  useEffect(() => {
    setTooltipVisible({});
  }, [selectedDate]);

  useEffect(() => {
    const timers = [];
    Object.entries(yesterdayMeals).forEach(([mealKey, entries]) => {
      if (entries && entries.length > 0 && tooltipVisible[mealKey] === undefined) {
        setTooltipVisible(prev => ({ ...prev, [mealKey]: true }));
        const timer = setTimeout(() => {
          setTooltipVisible(prev => ({ ...prev, [mealKey]: false }));
        }, 3000);
        timers.push(timer);
      }
    });

    return () => timers.forEach(timer => clearTimeout(timer));
  }, [yesterdayMeals, selectedDate]);

  // Load Training Log and Afterburn data (NEW)
  const loadTrainingLog = async () => {
    if (!portalToken) return;
    try {
      // Load training log for today
      const response = await axios.get(`${API}/portal/nutrition/training-log/today`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setTrainingLog(response.data);
      setAfterburnCalories(response.data?.afterburn_calories || 0);
      setActivityVariant(response.data?.activity_variant || 'weekly');
    } catch (error) {
      console.error('Error loading training log:', error);
      // Set default values
      setTrainingLog(null);
      setAfterburnCalories(0);
    }
  };

  const loadMealDistribution = async () => {
    if (!portalToken) return;
    try {
      const response = await axios.get(`${API}/portal/meal-distribution`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setMealDistribution(response.data);
      setEditMealForm({
        breakfast: response.data.breakfast,
        lunch: response.data.lunch,
        dinner: response.data.dinner,
        snacks: response.data.snacks
      });
    } catch (error) {
      console.error('Error loading meal distribution:', error);
      setMealDistribution({
        breakfast: 30, lunch: 35, dinner: 25, snacks: 10,
        daily_calorie_goal: 2000,
        calories: { breakfast: 600, lunch: 700, dinner: 500, snacks: 200 }
      });
    }
  };

  // Save meal distribution
  const saveMealDistribution = async () => {
    if (!portalToken) return;
    setSavingMealDist(true);
    try {
      await axios.put(
        `${API}/portal/meal-distribution`,
        editMealForm,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      toast.success('Verteilung gespeichert');
      setShowMealDistributionEdit(false);
      loadMealDistribution();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingMealDist(false);
    }
  };

  // Reset to default
  const resetMealDistribution = async () => {
    if (!portalToken) return;
    setSavingMealDist(true);
    try {
      await axios.delete(`${API}/portal/meal-distribution`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      toast.success('Auf Standard zurückgesetzt');
      setShowMealDistributionEdit(false);
      loadMealDistribution();
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen');
    } finally {
      setSavingMealDist(false);
    }
  };

  const loadNutritionData = async () => {
    if (!portalToken) return;
    
    setLoading(true);
    try {
      // Load today's summary with selected date
      const summaryResponse = await axios.get(`${API}/portal/nutrition/summary`, {
        params: { date: selectedDate },
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setNutritionData(summaryResponse.data);
      
      // Auto-show onboarding for first-time users (no entries and default goals)
      const goals = summaryResponse.data?.goals;
      const hasEntries = Object.values(summaryResponse.data?.meals || {}).some(m => m?.length > 0);
      const totalCalories = summaryResponse.data?.total_calories || 0;
      if (!hasEntries && totalCalories === 0 && !localStorage.getItem('nutrition_onboarding_seen')) {
        setShowNutritionOnboarding(true);
      }
      
      // Extract water data
      setWaterIntake(summaryResponse.data?.water_intake || 0);
      setWaterGoal(summaryResponse.data?.water_goal || 2500);
    } catch (error) {
      console.error('Error loading nutrition data:', error);
      // Set default data
      setNutritionData({
        total_calories: 0,
        total_protein: 0,
        total_carbs: 0,
        total_fat: 0,
        goals: { daily_calories: 2000, daily_protein: 120, daily_carbs: 200, daily_fat: 65 },
        meals: {},
        micronutrients: {}
      });
    } finally {
      setLoading(false);
    }
  };

  // Add water
  const addWater = async (ml) => {
    const newIntake = Math.min(waterIntake + ml, waterGoal + 1000);
    setWaterIntake(newIntake);
    toast.success(`+${ml}ml Wasser hinzugefügt`);
    
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    
    // Save to backend
    try {
      await axios.post(
        `${API}/portal/nutrition/entries`,
        {
          date: today,
          meal_type: 'water',
          food_name: 'Wasser',
          serving_size: ml,
          calories: 0,
          protein: 0,
          carbs: 0,
          fat: 0,
        },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
    } catch (error) {
      console.error('Error logging water:', error);
    }
  };

  // Calculate percentages
  const calculatePercent = (current, goal) => {
    if (!goal) return 0;
    return Math.min(Math.round((current / goal) * 100), 100);
  };

  // Get meal entries for display
  const getMealData = (mealType) => {
    const meals = nutritionData?.meals || {};
    const mealEntries = meals[mealType] || [];
    
    if (mealEntries.length === 0) {
      return { logged: false, items: null, kcal: 0 };
    }
    
    const totalKcal = mealEntries.reduce((sum, e) => sum + (e.calories || 0), 0);
    const itemNames = mealEntries.map(e => e.food_name).slice(0, 2).join(', ');
    
    return { logged: true, items: itemNames, kcal: totalKcal };
  };

  // Open meal menu for specific meal (instead of directly going to food search)
  const openMealMenuForMeal = (mealType) => {
    setSelectedMealType(mealType);
    setShowAddMealMenu(true);
  };
  
  // Open food modal for specific meal (used by menu option)
  const openFoodSearchForMeal = (mealType) => {
    setSelectedMealType(mealType);
    setShowAddMealMenu(false);
    setShowFoodSearch(true);
  };
  
  // Open KI calculator
  const openMealCalculator = () => {
    setShowAddMealMenu(false);
    setShowMealCalculator(true);
  };
  
  // Open photo analyzer
  const openPhotoAnalyzer = () => {
    setShowAddMealMenu(false);
    setShowPhotoAnalyzer(true);
  };
  
  // Open barcode scanner
  const openBarcodeScanner = () => {
    setShowAddMealMenu(false);
    setShowBarcodeScanner(true);
  };
  
  // Handle meal calculated from KI
  const handleMealCalculated = (result) => {
    // Result contains portions with ingredients, grams, calories, protein, carbs, fat
    // Create nutrition entries for each portion
    if (result && result.portions) {
      result.portions.forEach(async (portion) => {
        try {
          await axios.post(
            `${API}/portal/nutrition/entries`,
            {
              date: selectedDate,
              meal_type: selectedMealType,
              food_name: `${portion.ingredient} (${portion.grams}g)`,
              serving_size: portion.grams,
              calories: portion.calories,
              protein: portion.protein,
              carbs: portion.carbs,
              fat: portion.fat
            },
            { headers: { Authorization: `Bearer ${portalToken}` } }
          );
        } catch (error) {
          console.error('Error creating entry:', error);
        }
      });
      toast.success('Mahlzeit hinzugefügt!');
      loadNutritionData();
    }
  };

  // Delete a single nutrition entry
  const handleDeleteEntry = async (entryId) => {
    try {
      await axios.delete(`${API}/portal/nutrition/entries/${entryId}`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      toast.success('Eintrag gelöscht');
      loadNutritionData();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  // Update serving size of an entry (recalculates macros proportionally)
  const handleUpdateEntry = async (entry) => {
    const newSize = parseFloat(editServingSize);
    if (!newSize || newSize <= 0) return;
    
    const oldSize = parseFloat(entry.serving_size) || 100;
    const factor = newSize / oldSize;
    
    try {
      await axios.put(`${API}/portal/nutrition/entries/${entry.id}`, {
        serving_size: newSize,
        calories: Math.round((entry.calories || 0) * factor),
        protein: Math.round(((entry.protein || 0) * factor) * 10) / 10,
        carbs: Math.round(((entry.carbs || 0) * factor) * 10) / 10,
        fat: Math.round(((entry.fat || 0) * factor) * 10) / 10,
      }, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      toast.success('Eintrag aktualisiert');
      setEditingEntry(null);
      setEditServingSize('');
      loadNutritionData();
    } catch (error) {
      console.error('Error updating entry:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Toggle meal expansion
  const toggleMealExpanded = (mealKey) => {
    setExpandedMeals(prev => ({ ...prev, [mealKey]: !prev[mealKey] }));
  };

  // Handle food added
  const handleFoodAdded = () => {
    loadNutritionData();
  };

  // Nutrition goals
  const goals = nutritionData?.goals || { daily_calories: 2000, daily_protein: 120, daily_carbs: 200, daily_fat: 65 };
  const totals = {
    calories: nutritionData?.total_calories || 0,
    protein: nutritionData?.total_protein || 0,
    carbs: nutritionData?.total_carbs || 0,
    fat: nutritionData?.total_fat || 0,
  };

  // Meal types for display
  const mealTypes = [
    { key: 'breakfast', label: t('nutrition.meals.breakfast') },
    { key: 'snack_morning', label: t('nutrition.meals.snack_morning') },
    { key: 'lunch', label: t('nutrition.meals.lunch') },
    { key: 'snack_afternoon', label: t('nutrition.meals.snack_afternoon') },
    { key: 'dinner', label: t('nutrition.meals.dinner') },
    { key: 'snack_evening', label: t('nutrition.meals.snack_evening') },
  ];

  return (
    <>
    {/* Header with Day Navigation */}
    <div className="p-4 pb-0 pt-6">
      <div className="flex items-center justify-between">
        <h1 className="text-white text-xl font-bold">{t('nutrition.title')}</h1>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowNutritionSettings(true)}
            className="p-2 bg-zinc-800 rounded-lg"
            data-testid="nutrition-settings-btn"
          >
            <Settings className="w-4 h-4 text-yellow-400" />
          </button>
          <button 
            onClick={loadNutritionData}
            className="p-2 bg-zinc-800 rounded-lg"
          >
            <RefreshCw className={`w-4 h-4 text-zinc-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Sub-Tab Navigation */}
      <div className="flex gap-1 mt-3 bg-zinc-800/50 rounded-xl p-1" data-testid="nutrition-sub-tabs">
        {[
          { id: 'food', label: t('nutrition.tab_food') },
          { id: 'training', label: t('nutrition.tab_training') },
          { id: 'analytics', label: t('nutrition.tab_analytics') },
          { id: 'recovery', label: t('nutrition.tab_recovery') },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
              activeSubTab === tab.id
                ? 'bg-yellow-500 text-black shadow-lg'
                : 'text-zinc-400 hover:text-zinc-300'
            }`}
            data-testid={`nutrition-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>

    {/* Tab Content */}
    {activeSubTab === 'training' ? (
      <MobileTrainingTab portalToken={portalToken} selectedDate={selectedDate} />
    ) : activeSubTab === 'analytics' ? (
      <MobileAnalyticsTab portalToken={portalToken} />
    ) : activeSubTab === 'recovery' ? (
      <MobileRecoveryTab portalToken={portalToken} selectedDate={selectedDate} />
    ) : (
    <>
    {/* Main scrollable content - Essen Tab */}
    <div className="p-4 space-y-4 pb-32">
      {/* ===================== TAGESANZEIGE (Collapsible with Swipe Date) ===================== */}
      <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-2xl border border-green-500/30 overflow-hidden">
        {/* Header with Date Navigation - Swipeable */}
        <div 
          onClick={() => setShowMacroSection(!showMacroSection)}
          className="w-full p-3 cursor-pointer"
          role="button"
          tabIndex={0}
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Scale className="w-4 h-4 text-green-400" />
              <span className="text-zinc-300 text-sm">{t('nutrition.daily_overview')}</span>
            </div>
            <div className="flex items-center gap-1">
              <button 
                onClick={(e) => { e.stopPropagation(); goToPreviousDay(); }}
                className="p-1 hover:bg-zinc-700/50 rounded transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-zinc-400" />
              </button>
              <span className="text-zinc-400 text-xs min-w-[60px] text-center">{formatDateDisplay(selectedDate)}</span>
              <button 
                onClick={(e) => { e.stopPropagation(); goToNextDay(); }}
                className="p-1 hover:bg-zinc-700/50 rounded transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-zinc-400" />
              </button>
              <ChevronRight className={`w-4 h-4 text-zinc-400 transition-transform ml-1 ${showMacroSection ? 'rotate-90' : ''}`} />
            </div>
          </div>
          {/* Compact calorie summary always visible */}
          <div className="flex justify-between items-center mt-2">
            <div className="h-1.5 flex-1 bg-zinc-700 rounded-full overflow-hidden mr-3">
              <div 
                className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all" 
                style={{ width: `${calculatePercent(totals.calories, goals.daily_calories)}%` }} 
              />
            </div>
            <span className="text-green-400 text-xs font-medium whitespace-nowrap">
              {Math.round(totals.calories || 0).toLocaleString()} / {Math.round(goals.daily_calories || 0).toLocaleString()} kcal
            </span>
          </div>
        </div>
        
        {/* Expanded Macro Circles */}
        {showMacroSection && (
          <div className="px-3 pb-3">
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: t('nutrition.protein'), value: totals.protein, max: goals.daily_protein, color: 'text-blue-500' },
                { label: t('nutrition.carbs'), value: totals.carbs, max: goals.daily_carbs, color: 'text-orange-500' },
                { label: t('nutrition.fat'), value: totals.fat, max: goals.daily_fat, color: 'text-amber-500' },
              ].map(macro => {
                const percent = calculatePercent(macro.value, macro.max);
                return (
                  <div key={macro.label} className="text-center">
                    <div className="relative w-12 h-12 mx-auto">
                      <svg className="w-12 h-12 transform -rotate-90">
                        <circle cx="24" cy="24" r="20" fill="none" stroke="#3f3f46" strokeWidth="3" />
                        <circle 
                          cx="24" cy="24" r="20" fill="none" strokeWidth="3"
                          className={macro.color}
                          stroke="currentColor"
                          strokeDasharray={`${percent * 1.25} 125`} 
                        />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-[10px] text-white font-medium">
                        {percent}%
                      </span>
                    </div>
                    <p className="text-zinc-400 text-[10px] mt-0.5">{macro.label}</p>
                    <p className="text-white text-[10px]">{Math.round(macro.value)}g/{macro.max}g</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ===================== ERNÄHRUNGS-LOG (Collapsible - Compact) ===================== */}
      <div className="bg-zinc-800/50 rounded-full overflow-hidden">
        <button 
          onClick={() => setShowNutritionLog(!showNutritionLog)}
          className="w-full flex justify-between items-center px-4 py-2"
        >
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-yellow-400" />
            <span className="text-zinc-300 text-sm">{t('nutrition.food_log')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-zinc-500 text-xs">{formatDateDisplay(selectedDate)}</span>
            <ChevronRight className={`w-4 h-4 text-zinc-400 transition-transform ${showNutritionLog ? 'rotate-90' : ''}`} />
          </div>
        </button>
      </div>
      
      {showNutritionLog && (
        <div className="bg-zinc-800/30 rounded-2xl p-2 space-y-1">
        <p className="text-[10px] text-zinc-500 px-2" data-testid="mobile-nutrition-copy-hint">
          Hinweis: „Wie gestern“ erscheint, sobald Vortag‑Einträge vorhanden sind. Bei bestehenden Einträgen fragen wir nach.
        </p>
        {mealTypes.map((meal) => {
          const data = getMealData(meal.key);
          const mealEntries = (nutritionData?.meals || {})[meal.key] || [];
          const hasYesterdayData = yesterdayMeals[meal.key] && yesterdayMeals[meal.key].length > 0;
          const isExpanded = expandedMeals[meal.key];
          return (
            <div key={meal.key} className="py-1 border-b border-zinc-700/30 last:border-0">
              {/* Meal header row */}
              <div className="w-full flex items-center justify-between hover:bg-zinc-700/20 transition-colors rounded-lg px-1 py-0.5">
                <button
                  onClick={() => data.logged ? toggleMealExpanded(meal.key) : openMealMenuForMeal(meal.key)}
                  className="flex items-center gap-2 flex-1 min-w-0"
                  data-testid={`meal-row-${meal.key}`}
                >
                  <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
                    data.logged ? 'bg-yellow-500/20' : 'bg-zinc-700'
                  }`}>
                    <Utensils className={`w-3.5 h-3.5 ${data.logged ? 'text-yellow-400' : 'text-zinc-400'}`} />
                  </div>
                  <div className="text-left min-w-0">
                    <p className="text-white text-xs font-medium">{meal.label}</p>
                    {data.logged && !isExpanded && (
                      <p className="text-zinc-500 text-[10px] truncate max-w-[120px]">
                        {data.items}
                      </p>
                    )}
                    {!data.logged && (
                      <p className="text-zinc-500 text-[10px]">Tippen</p>
                    )}
                  </div>
                </button>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {data.logged ? (
                    <>
                      <span className="text-yellow-400 text-xs font-medium">{Math.round(data.kcal || 0)} kcal</span>
                      <button
                        onClick={() => toggleMealExpanded(meal.key)}
                        className="p-0.5"
                        data-testid={`meal-expand-${meal.key}`}
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-3.5 h-3.5 text-zinc-400" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 text-zinc-400" />
                        )}
                      </button>
                    </>
                  ) : (
                    <button onClick={() => openMealMenuForMeal(meal.key)} className="p-0.5">
                      <Plus className="w-4 h-4 text-yellow-500" />
                    </button>
                  )}
                </div>
              </div>

              {/* Expanded entries with edit/delete + swipe */}
              {isExpanded && mealEntries.length > 0 && (
                <div className="ml-9 mt-1 space-y-1 mb-1">
                  {mealEntries.map((entry) => (
                    <SwipeableEntry
                      key={entry.id}
                      entry={entry}
                      editingEntry={editingEntry}
                      editServingSize={editServingSize}
                      setEditServingSize={setEditServingSize}
                      setEditingEntry={setEditingEntry}
                      handleUpdateEntry={handleUpdateEntry}
                      handleDeleteEntry={handleDeleteEntry}
                    />
                  ))}
                  <button
                    onClick={() => openMealMenuForMeal(meal.key)}
                    className="w-full flex items-center justify-center gap-1 py-1 rounded-lg border border-dashed border-zinc-700 text-zinc-500 text-[10px] hover:border-yellow-500/50 hover:text-yellow-400 transition-colors"
                    data-testid={`meal-add-more-${meal.key}`}
                  >
                    <Plus className="w-3 h-3" /> Hinzufügen
                  </button>
                </div>
              )}

              {!hasYesterdayData ? null : (
                <div className="ml-9 mt-1 flex items-center gap-2" data-testid={`copy-yesterday-tooltip-wrapper-${meal.key}`}>
                  {tooltipVisible[meal.key] && (
                    <span className="text-[10px] text-zinc-500" data-testid={`copy-yesterday-tooltip-${meal.key}`}>
                      Tippe „Wie gestern“, um die Einträge zu übernehmen.
                    </span>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); requestCopyYesterday(meal.key, data.logged); }}
                    disabled={copyingMeal[meal.key]}
                    className="px-2 py-1 bg-blue-500/10 border border-blue-500/30 rounded text-blue-400 text-[10px] flex items-center gap-1 hover:bg-blue-500/20 transition-all disabled:opacity-50"
                    data-testid={`copy-yesterday-${meal.key}`}
                  >
                    {copyingMeal[meal.key] ? (
                      <RefreshCw className="w-2.5 h-2.5 animate-spin" />
                    ) : (
                      <Clock className="w-2.5 h-2.5" />
                    )}
                    Wie gestern ({yesterdayMeals[meal.key].length})
                  </button>
                </div>
              )}
            </div>
          );
        })}
        </div>
      )}

      {/* ===================== WASSER (Collapsible - Compact) ===================== */}
      <div className="bg-zinc-800/50 rounded-full overflow-hidden">
        <button 
          onClick={() => setShowWaterSection(!showWaterSection)}
          className="w-full flex justify-between items-center px-4 py-2"
        >
          <div className="flex items-center gap-2">
            <Droplet className="w-4 h-4 text-cyan-400" />
            <span className="text-zinc-300 text-sm">{t('nutrition.water')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-cyan-400 text-xs">{(waterIntake / 1000).toFixed(1)} / {(waterGoal / 1000).toFixed(1)} L</span>
            <ChevronRight className={`w-4 h-4 text-zinc-400 transition-transform ${showWaterSection ? 'rotate-90' : ''}`} />
          </div>
        </button>
      </div>
      
      {showWaterSection && (
        <div className="bg-zinc-800/30 rounded-2xl p-3">
          <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-cyan-500 rounded-full transition-all" 
              style={{ width: `${Math.min((waterIntake / waterGoal) * 100, 100)}%` }} 
            />
          </div>
          <div className="flex gap-2 mt-2">
            {[250, 500].map(ml => (
              <button 
                key={ml}
                onClick={() => addWater(ml)}
                className="flex-1 py-1.5 bg-cyan-500/20 rounded-lg text-cyan-400 text-xs hover:bg-cyan-500/30 transition-colors"
              >
                +{ml}ml
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ===================== TRAINING & NACHBRENN-EFFEKT (Collapsible - Compact) ===================== */}
      {activityVariant === 'daily' && (
        <>
        <div className="bg-zinc-800/50 rounded-full overflow-hidden">
          <button 
            onClick={() => setShowTrainingSection(!showTrainingSection)}
            className="w-full flex justify-between items-center px-4 py-2"
          >
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-orange-500" />
              <span className="text-zinc-300 text-sm">Training & Nachbrenn</span>
            </div>
            <div className="flex items-center gap-2">
              {afterburnCalories > 0 && (
                <span className="text-orange-400 text-xs">+{afterburnCalories} kcal</span>
              )}
              <ChevronRight className={`w-4 h-4 text-zinc-400 transition-transform ${showTrainingSection ? 'rotate-90' : ''}`} />
            </div>
          </button>
        </div>
          
        {showTrainingSection && (
          <div className="bg-zinc-800/30 rounded-2xl p-3 space-y-2">
            {/* Afterburn Calories Display */}
            {afterburnCalories > 0 && (
              <div className="flex items-center gap-2 p-2 bg-orange-500/10 rounded-xl border border-orange-500/30">
                <span className="text-base">🔥</span>
                <div className="flex-1">
                  <p className="text-orange-400 text-[10px]">Nachbrenn-Effekt vom Vortag</p>
                  <p className="text-white text-sm font-bold">+{afterburnCalories} kcal</p>
                </div>
              </div>
            )}
            
            {/* Training Log Details */}
            {trainingLog && (
              <div className="space-y-1">
                {trainingLog.booked_trainings?.length > 0 && (
                  <div>
                    <p className="text-zinc-400 text-[10px] mb-1">📅 Gebuchte Trainings</p>
                    {trainingLog.booked_trainings.map((t, idx) => (
                      <div key={idx} className="flex items-center justify-between p-1.5 bg-zinc-900/50 rounded-lg mb-1">
                        <div className="flex items-center gap-1">
                          <span className="text-green-400 text-xs">✓</span>
                          <span className="text-white text-xs">{t.service_name}</span>
                        </div>
                        <span className="text-orange-400 text-xs">+{t.calories_burned || 0} kcal</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {trainingLog.manual_entries?.length > 0 && (
                  <div>
                    <p className="text-zinc-400 text-[10px] mb-1">✏️ Manuelle Aktivitäten</p>
                    {trainingLog.manual_entries.map((e, idx) => (
                      <div key={idx} className="flex items-center justify-between p-1.5 bg-zinc-900/50 rounded-lg mb-1">
                        <div className="flex items-center gap-1">
                          <span className="text-xs">{e.activity_type === 'cardio' ? '🏃' : e.activity_type === 'strength' ? '💪' : '🧘'}</span>
                          <span className="text-white text-xs">{e.activity_name || e.activity_type}</span>
                        </div>
                        <span className="text-orange-400 text-xs">+{e.calories_burned || 0} kcal</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {(!trainingLog.booked_trainings?.length && !trainingLog.manual_entries?.length) && (
                  <p className="text-zinc-500 text-[10px] text-center py-1">Noch keine Trainings heute</p>
                )}
                
                <div className="pt-1 border-t border-zinc-700/50">
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-400">Gesamt:</span>
                    <span className="text-orange-400 font-medium">{trainingLog.total_calories_burned || 0} kcal</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        </>
      )}

      {/* ===================== MAHLZEITEN-VERTEILUNG (Collapsible - Compact) ===================== */}
      <div className="bg-zinc-800/50 rounded-full overflow-hidden">
        <button 
          onClick={() => setShowMealDistribution(!showMealDistribution)}
          className="w-full flex justify-between items-center px-4 py-2"
        >
          <div className="flex items-center gap-2">
            <Utensils className="w-4 h-4 text-orange-400" />
            <span className="text-zinc-300 text-sm">{t('nutrition.meal_distribution')}</span>
          </div>
          <ChevronRight className={`w-4 h-4 text-zinc-400 transition-transform ${showMealDistribution ? 'rotate-90' : ''}`} />
        </button>
      </div>
        
      {showMealDistribution && mealDistribution && (
        <div className="bg-zinc-800/30 rounded-2xl p-3 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-zinc-400 text-[10px] font-medium">Tagesziel: {mealDistribution.daily_calorie_goal} kcal</span>
            {mealDistribution.is_custom && (
              <span className="text-[10px] px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded">Individuell</span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: 'breakfast', label: 'Frühstück', icon: '☀️', mealKeys: ['breakfast'] },
              { key: 'lunch', label: 'Mittag', icon: '🌤️', mealKeys: ['lunch'] },
              { key: 'dinner', label: 'Abend', icon: '🌙', mealKeys: ['dinner'] },
              { key: 'snacks', label: 'Snacks', icon: '🍪', mealKeys: ['snack_morning', 'snack_afternoon', 'snack_evening', 'snack'] },
            ].map(meal => {
              const target = mealDistribution.calories?.[meal.key] || 0;
              const consumed = meal.mealKeys.reduce((sum, mk) => {
                const meals = nutritionData?.meals?.[mk] || [];
                return sum + meals.reduce((s, m) => s + (m.calories || 0), 0);
              }, 0);
              const percent = target > 0 ? Math.round((consumed / target) * 100) : 0;
              const isOver = percent > 100;
              
              return (
                <div 
                  key={meal.key} 
                  className={`p-2 rounded-lg ${isOver ? 'bg-red-500/10 border border-red-500/30' : 'bg-zinc-900/50'}`}
                >
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-sm">{meal.icon}</span>
                    <span className="text-white text-xs font-medium">{meal.label}</span>
                  </div>
                  <div className="h-1 bg-zinc-700 rounded-full overflow-hidden mb-1">
                    <div 
                      className={`h-full rounded-full transition-all ${isOver ? 'bg-red-500' : 'bg-orange-500'}`}
                      style={{ width: `${Math.min(percent, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400 text-[9px]">{consumed}/{target}</span>
                    <span className={`text-[9px] font-medium ${isOver ? 'text-red-400' : 'text-zinc-400'}`}>
                      {percent}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Edit/Reset Buttons */}
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => setShowMealDistributionEdit(true)}
              className="flex-1 py-1.5 px-2 bg-orange-500/20 text-orange-400 text-[10px] font-medium rounded-lg"
              data-testid="edit-meal-distribution-btn"
            >
              Anpassen
            </button>
            {mealDistribution.is_custom && (
              <button
                onClick={resetMealDistribution}
                disabled={savingMealDist}
                className="py-1.5 px-2 bg-zinc-700 text-zinc-300 text-[10px] font-medium rounded-lg disabled:opacity-50"
                data-testid="reset-meal-distribution-btn"
              >
                {savingMealDist ? '...' : 'Standard'}
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Mahlzeiten-Verteilung Edit Modal */}
      {showMealDistributionEdit && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end">
          <div className="w-full bg-zinc-900 rounded-t-3xl p-5 space-y-4 animate-slide-up">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-semibold">Verteilung anpassen</h3>
              <button 
                onClick={() => setShowMealDistributionEdit(false)}
                className="text-zinc-400 text-lg"
              >
                ✕
              </button>
            </div>
            
            <p className="text-zinc-400 text-xs">
              Legen Sie fest, wie Ihre Tageskalorien auf die Mahlzeiten verteilt werden sollen.
            </p>
            
            {[
              { key: 'breakfast', label: 'Frühstück', icon: '☀️' },
              { key: 'lunch', label: 'Mittagessen', icon: '🌤️' },
              { key: 'dinner', label: 'Abendessen', icon: '🌙' },
              { key: 'snacks', label: 'Snacks', icon: '🍪' },
            ].map(meal => (
              <div key={meal.key} className="flex items-center gap-3">
                <span className="text-lg w-8">{meal.icon}</span>
                <span className="text-white text-sm flex-1">{meal.label}</span>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={editMealForm[meal.key]}
                    onChange={(e) => setEditMealForm({
                      ...editMealForm,
                      [meal.key]: Math.max(0, Math.min(100, parseInt(e.target.value) || 0))
                    })}
                    className="w-16 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-white text-sm text-center"
                  />
                  <span className="text-zinc-400 text-sm">%</span>
                </div>
              </div>
            ))}
            
            {/* Total */}
            <div className={`p-3 rounded-lg ${
              (editMealForm.breakfast + editMealForm.lunch + editMealForm.dinner + editMealForm.snacks) > 100
                ? 'bg-yellow-500/20 border border-yellow-500/30'
                : 'bg-zinc-800'
            }`}>
              <div className="flex justify-between items-center">
                <span className="text-zinc-400 text-sm">Gesamt</span>
                <span className="text-white text-lg font-bold">
                  {editMealForm.breakfast + editMealForm.lunch + editMealForm.dinner + editMealForm.snacks}%
                </span>
              </div>
              {(editMealForm.breakfast + editMealForm.lunch + editMealForm.dinner + editMealForm.snacks) > 100 && (
                <p className="text-yellow-500 text-[10px] mt-1">
                  Über 100% = Sie essen mehr als Ihr Tagesziel
                </p>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              {mealDistribution?.is_custom && (
                <button
                  onClick={resetMealDistribution}
                  disabled={savingMealDist}
                  className="py-3 px-4 bg-zinc-800 text-zinc-300 text-sm font-medium rounded-xl disabled:opacity-50"
                  data-testid="modal-reset-btn"
                >
                  Auf Standard
                </button>
              )}
              <button
                onClick={saveMealDistribution}
                disabled={savingMealDist}
                className="flex-1 py-3 px-4 bg-orange-500 text-white text-sm font-medium rounded-xl disabled:opacity-50"
                data-testid="modal-save-btn"
              >
                {savingMealDist ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== MIKRONÄHRSTOFFE (Collapsible - Compact) ===================== */}
      <div className="bg-zinc-800/50 rounded-full overflow-hidden">
        <button 
          onClick={() => setShowMicronutrients(!showMicronutrients)}
          className="w-full flex justify-between items-center px-4 py-2"
        >
          <div className="flex items-center gap-2">
            <Pill className="w-4 h-4 text-green-400" />
            <span className="text-zinc-300 text-sm">{t('nutrition.micronutrients')}</span>
          </div>
          <ChevronRight className={`w-4 h-4 text-zinc-400 transition-transform ${showMicronutrients ? 'rotate-90' : ''}`} />
        </button>
      </div>
        
      {showMicronutrients && nutritionData?.micronutrients && (
        <div className="bg-zinc-800/30 rounded-2xl p-3 space-y-2">
          <div className="text-zinc-400 text-[10px] font-medium">Vitamine</div>
          <div className="grid grid-cols-4 gap-1">
            {[
              { key: 'vitamin_c', label: 'C', unit: 'mg', goal: 100 },
              { key: 'vitamin_d', label: 'D', unit: 'µg', goal: 20 },
              { key: 'vitamin_b12', label: 'B12', unit: 'µg', goal: 4 },
              { key: 'vitamin_a', label: 'A', unit: 'µg', goal: 900 },
            ].map(vit => (
              <div key={vit.key} className="text-center p-1 bg-zinc-900/50 rounded">
                <div className="text-white text-xs font-medium">
                  {Math.round(nutritionData.micronutrients[vit.key] || 0)}
                </div>
                <div className="text-zinc-500 text-[9px]">{vit.label}</div>
              </div>
            ))}
          </div>
          <div className="text-zinc-400 text-[10px] font-medium mt-1">Mineralien</div>
          <div className="grid grid-cols-4 gap-1">
            {[
              { key: 'calcium', label: 'Ca', unit: 'mg', goal: 1000 },
              { key: 'iron', label: 'Fe', unit: 'mg', goal: 15 },
              { key: 'magnesium', label: 'Mg', unit: 'mg', goal: 400 },
              { key: 'zinc', label: 'Zn', unit: 'mg', goal: 10 },
            ].map(min => (
              <div key={min.key} className="text-center p-1 bg-zinc-900/50 rounded">
                <div className="text-white text-xs font-medium">
                  {Math.round(nutritionData.micronutrients[min.key] || 0)}
                </div>
                <div className="text-zinc-500 text-[9px]">{min.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===================== KI-TAGESANALYSE (Button) ===================== */}
      <div className="bg-zinc-800/50 rounded-full overflow-hidden">
        <button 
          onClick={() => setShowDayAnalysis(true)}
          className="w-full flex justify-between items-center px-4 py-2"
          data-testid="mobile-day-analysis-btn"
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-yellow-400" />
            <span className="text-zinc-300 text-sm">{t('nutrition.ai_analysis')}</span>
          </div>
          <ChevronRight className="w-4 h-4 text-zinc-400" />
        </button>
      </div>

      {/* ===================== EINKAUFSLISTE (Collapsible - Compact, at bottom) ===================== */}
      <div className="bg-zinc-800/50 rounded-full overflow-hidden">
        <button 
          onClick={() => onNavigate('shopping')}
          className="w-full flex justify-between items-center px-4 py-2"
        >
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-cyan-400" />
            <span className="text-zinc-300 text-sm">{t('nutrition.shopping_list')}</span>
          </div>
          <ChevronRight className="w-4 h-4 text-zinc-400" />
        </button>
      </div>

      {/* Photo History */}
      <div className="bg-zinc-800/30 rounded-xl overflow-hidden">
        <button 
          onClick={() => onNavigate('photo-history')}
          className="w-full flex justify-between items-center px-4 py-2"
          data-testid="photo-history-link"
        >
          <div className="flex items-center gap-2">
            <Image className="w-4 h-4 text-pink-400" />
            <span className="text-zinc-300 text-sm">{t('photo_history.title')}</span>
          </div>
          <ChevronRight className="w-4 h-4 text-zinc-400" />
        </button>
      </div>
    </div>

    {/* End of Essen Tab content */}
    </>
    )}

    {/* ===================== MODALS CONTAINER (Outside scrollable area) ===================== */}
      
    {/* Add Meal Menu - Choose input method */}
    {showAddMealMenu && (
      <div className="absolute inset-0 bg-black/95 z-50 flex items-end">
        <div className="w-full bg-zinc-900 rounded-t-3xl p-5 animate-slide-up">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-bold text-lg">Mahlzeit hinzufügen</h3>
            <button 
              onClick={() => setShowAddMealMenu(false)}
                className="text-zinc-400 text-2xl"
              >
                ✕
              </button>
            </div>
            
            <p className="text-zinc-500 text-sm mb-4">
              Wie möchtest du deine Mahlzeit erfassen?
            </p>
            
            {/* Compact Grid Layout - 3 columns */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {/* KI-Kalkulator */}
              <button
                onClick={openMealCalculator}
                className="p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-xl flex flex-col items-center gap-1 hover:bg-yellow-500/30 transition-all"
              >
                <Sparkles className="w-6 h-6 text-yellow-400" />
                <span className="text-yellow-400 text-xs font-medium text-center">KI-Kalkulator</span>
              </button>
              
              {/* Photo Analyzer */}
              <button
                onClick={openPhotoAnalyzer}
                className="p-3 bg-purple-500/20 border border-purple-500/30 rounded-xl flex flex-col items-center gap-1 hover:bg-purple-500/30 transition-all"
              >
                <Camera className="w-6 h-6 text-purple-400" />
                <span className="text-purple-400 text-xs font-medium text-center">Foto</span>
              </button>
              
              {/* Food Search */}
              <button
                onClick={() => openFoodSearchForMeal(selectedMealType)}
                className="p-3 bg-zinc-800/50 border border-zinc-700 rounded-xl flex flex-col items-center gap-1 hover:bg-zinc-700/50 transition-all"
              >
                <Search className="w-6 h-6 text-zinc-300" />
                <span className="text-zinc-300 text-xs font-medium text-center">Suchen</span>
              </button>
              
              {/* Barcode Scanner */}
              <button
                onClick={openBarcodeScanner}
                className="p-3 bg-zinc-800/50 border border-zinc-700 rounded-xl flex flex-col items-center gap-1 hover:bg-zinc-700/50 transition-all"
              >
                <FileText className="w-6 h-6 text-zinc-300" />
                <span className="text-zinc-300 text-xs font-medium text-center">Barcode</span>
              </button>
              
              {/* Voice Input */}
              <button
                onClick={() => {
                  setShowAddMealMenu(false);
                  setShowVoiceInput(true);
                }}
                className="p-3 bg-green-500/20 border border-green-500/30 rounded-xl flex flex-col items-center gap-1 hover:bg-green-500/30 transition-all"
              >
                <Mic className="w-6 h-6 text-green-400" />
                <span className="text-green-400 text-xs font-medium text-center">Sprache</span>
              </button>
              
              {/* Create Custom Meal */}
              <button
                onClick={() => {
                  setShowAddMealMenu(false);
                  setShowCreateMeal(true);
                }}
                className="p-3 bg-orange-500/20 border border-orange-500/30 rounded-xl flex flex-col items-center gap-1 hover:bg-orange-500/30 transition-all"
              >
                <UtensilsCrossed className="w-6 h-6 text-orange-400" />
                <span className="text-orange-400 text-xs font-medium text-center">Erstellen</span>
              </button>
            </div>
            
            {/* Tagesplaner - Full width special button */}
            <button
              onClick={() => {
                setShowAddMealMenu(false);
                setShowMealPlanner(true);
              }}
              className="w-full p-3 bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-xl flex items-center justify-center gap-2 hover:from-purple-500/30 hover:to-blue-500/30 transition-all"
            >
              <Calendar className="w-5 h-5 text-purple-400" />
              <span className="text-purple-400 text-sm font-medium">🍽️ Tagesplaner</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Meal Planner Screen - Full Day Planner */}
      <MealPlannerScreen
        isOpen={showMealPlanner}
        onClose={() => setShowMealPlanner(false)}
        portalToken={portalToken}
        customerId={customerId}
        dailyProgress={totals}
        dailyGoals={goals}
        onMealAdded={async (mealData) => {
          try {
            await axios.post(
              `${API}/portal/nutrition/entries`,
              {
                date: selectedDate,
                ...mealData
              },
              { headers: { Authorization: `Bearer ${portalToken}` } }
            );
            loadNutritionData();
            return true;
          } catch (error) {
            console.error('Error adding meal:', error);
            return false;
          }
        }}
      />
      
      {/* Meal Calculator Modal - KI-Kalkulator */}
      <MealCalculatorModal
        isOpen={showMealCalculator}
        onClose={() => setShowMealCalculator(false)}
        portalToken={portalToken}
        customerId={customerId}
        onMealCalculated={handleMealCalculated}
        remainingCalories={goals.daily_calories - totals.calories}
        remainingProtein={goals.daily_protein - totals.protein}
        remainingCarbs={goals.daily_carbs - totals.carbs}
        remainingFat={goals.daily_fat - totals.fat}
      />
      
      <FoodSearchModal 
        isOpen={showFoodSearch}
        onClose={() => setShowFoodSearch(false)}
        onSelectFood={handleFoodAdded}
        onCreateProduct={(barcode) => {
          setShowFoodSearch(false);
          setCreateProductBarcode(barcode || '');
          setShowCreateProduct(true);
        }}
        portalToken={portalToken}
        mealType={selectedMealType}
      />
      
      <BarcodeScanner 
        isOpen={showBarcodeScanner}
        onClose={() => setShowBarcodeScanner(false)}
        onProductFound={handleFoodAdded}
        onCreateProduct={(barcode) => {
          setShowBarcodeScanner(false);
          setCreateProductBarcode(barcode || '');
          setShowCreateProduct(true);
        }}
        portalToken={portalToken}
        mealType={selectedMealType}
      />
      
      <MealPhotoAnalyzer 
        isOpen={showPhotoAnalyzer}
        onClose={() => setShowPhotoAnalyzer(false)}
        onMealAdded={handleFoodAdded}
        portalToken={portalToken}
        customerId={customerId}
        mealType={selectedMealType}
      />
      
      <NutritionSettingsModal
        open={showNutritionSettings}
        onClose={() => setShowNutritionSettings(false)}
        token={portalToken}
        onSettingsChanged={loadNutritionData}
        onRestartOnboarding={() => {
          setShowNutritionSettings(false);
          setTimeout(() => setShowNutritionOnboarding(true), 300);
        }}
      />
      
      {/* Nutrition Onboarding Modal */}
      <NutritionOnboarding
        open={showNutritionOnboarding}
        onClose={() => setShowNutritionOnboarding(false)}
        token={portalToken}
        customerId={customerId}
        onComplete={() => {
          setShowNutritionOnboarding(false);
          localStorage.setItem('nutrition_onboarding_seen', 'true');
          loadNutritionData();
          toast.success('Ernährungsziele wurden gespeichert!');
        }}
      />
      
      {/* KI-Tagesanalyse Chat */}
      <MobileDayAnalysisChat
        isOpen={showDayAnalysis}
        onClose={() => setShowDayAnalysis(false)}
        portalToken={portalToken}
        date={selectedDate}
      />
      
      {/* Voice Input Modal - NEW like Kundenportal */}
      {showVoiceInput && (
        <div className="absolute inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-zinc-900 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-bold text-lg">🎤 Spracheingabe</h3>
              <button 
                onClick={resetVoiceInput}
                className="text-zinc-400 text-2xl hover:text-white"
              >
                ✕
              </button>
            </div>
            
            {voiceStep === 'record' && (
              <div className="text-center space-y-6">
                <p className="text-zinc-400 text-sm">
                  Sag einfach, was du gegessen hast. Z.B.:<br/>
                  <span className="text-zinc-300">"200 Gramm Hühnerbrust mit Reis und Brokkoli"</span>
                </p>
                
                {/* Recording Button */}
                <button
                  onClick={isRecording ? stopRecording : startRecording}
                  className={`w-24 h-24 rounded-full mx-auto flex items-center justify-center transition-all ${
                    isRecording 
                      ? 'bg-red-500 animate-pulse' 
                      : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
                  }`}
                >
                  {isRecording ? (
                    <MicOff className="w-10 h-10 text-white" />
                  ) : (
                    <Mic className="w-10 h-10 text-white" />
                  )}
                </button>
                
                {isRecording && (
                  <p className="text-red-400 font-medium">
                    Aufnahme läuft... {recordingTime}s
                  </p>
                )}
                
                <p className="text-zinc-500 text-xs">
                  {isRecording ? 'Tippe zum Stoppen' : 'Tippe zum Starten'}
                </p>
              </div>
            )}
            
            {voiceStep === 'confirm' && (
              <div className="space-y-4">
                {isProcessingVoice ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-12 h-12 text-yellow-400 animate-spin mx-auto mb-4" />
                    <p className="text-zinc-400">Verarbeite Spracheingabe...</p>
                  </div>
                ) : (
                  <>
                    {/* Transcription */}
                    <div className="bg-zinc-800 rounded-xl p-4">
                      <p className="text-zinc-400 text-xs mb-1">Du hast gesagt:</p>
                      <p className="text-white">{voiceTranscription}</p>
                    </div>
                    
                    {/* Detected Foods */}
                    {voiceDetectedFoods.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-zinc-400 text-sm">Erkannte Lebensmittel:</p>
                        {voiceDetectedFoods.map((food, idx) => (
                          <div key={idx} className="bg-zinc-800 rounded-xl p-3 flex justify-between items-center">
                            <div>
                              <p className="text-white font-medium">{food.name}</p>
                              <p className="text-zinc-500 text-xs">{food.portion_grams || 100}g</p>
                            </div>
                            <div className="text-right">
                              <p className="text-yellow-400 font-bold">{Math.round(food.calories || 0)} kcal</p>
                              <p className="text-zinc-500 text-xs">
                                P:{food.protein || 0}g K:{food.carbs || 0}g F:{food.fat || 0}g
                              </p>
                            </div>
                          </div>
                        ))}
                        
                        {/* Totals */}
                        <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-3 mt-2">
                          <div className="flex justify-between">
                            <span className="text-green-400 font-medium">Gesamt:</span>
                            <span className="text-white font-bold">{Math.round(voiceTotals.calories || 0)} kcal</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {voiceDetectedFoods.length === 0 && (
                      <div className="text-center py-4">
                        <p className="text-zinc-400">Keine Lebensmittel erkannt. Bitte versuche es erneut.</p>
                      </div>
                    )}
                    
                    {/* Meal Type Selection */}
                    <div>
                      <p className="text-zinc-400 text-sm mb-2">Mahlzeit:</p>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { key: 'breakfast', label: 'Frühstück', emoji: '🌅' },
                          { key: 'lunch', label: 'Mittag', emoji: '☀️' },
                          { key: 'dinner', label: 'Abend', emoji: '🌙' },
                        ].map(meal => (
                          <button
                            key={meal.key}
                            onClick={() => setSelectedMealType(meal.key)}
                            className={`p-2 rounded-lg text-center transition-all ${
                              selectedMealType === meal.key
                                ? 'bg-yellow-500 text-black'
                                : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                            }`}
                          >
                            <span className="text-lg">{meal.emoji}</span>
                            <p className="text-xs mt-1">{meal.label}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={() => setVoiceStep('record')}
                        className="flex-1 py-3 bg-zinc-800 text-zinc-300 rounded-xl font-medium hover:bg-zinc-700"
                      >
                        Nochmal
                      </button>
                      <button
                        onClick={confirmVoiceEntry}
                        disabled={voiceDetectedFoods.length === 0}
                        className="flex-1 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        <Check className="w-5 h-5" />
                        Speichern
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {copyConfirm.open && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70" data-testid="mobile-nutrition-copy-confirm-dialog">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-4 w-[90%] max-w-xs">
            <h3 className="text-white text-sm font-semibold">Vortag übernehmen?</h3>
            <p className="text-zinc-400 text-xs mt-2">
              Du hast bereits Einträge in {copyConfirm.mealLabel}. Beim Übernehmen werden {copyConfirm.count || 'zusätzliche'} Einträge hinzugefügt.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="text-xs px-3 py-1 rounded border border-zinc-600 text-zinc-300"
                onClick={closeCopyConfirm}
                data-testid="mobile-nutrition-copy-cancel"
              >
                Abbrechen
              </button>
              <button
                className="text-xs px-3 py-1 rounded bg-yellow-500 text-black"
                onClick={confirmCopyYesterday}
                data-testid="mobile-nutrition-copy-accept"
              >
                Trotzdem übernehmen
              </button>
            </div>
          </div>
        </div>
      )}

            {/* Create Meal Modal */}
      {showCreateMeal && (
        <CreateMealModal
          isOpen={showCreateMeal}
          onClose={() => setShowCreateMeal(false)}
          portalToken={portalToken}
          selectedDate={selectedDate}
          onMealCreated={() => {
            loadNutritionData();
            setShowCreateMeal(false);
          }}
        />
      )}

      {showCreateProduct && (
        <CreateProductWizard
          isOpen={showCreateProduct}
          onClose={() => {
            setShowCreateProduct(false);
            setCreateProductBarcode('');
          }}
          portalToken={portalToken}
          initialBarcode={createProductBarcode}
          onProductCreated={() => {
            loadNutritionData();
            setShowCreateProduct(false);
            setCreateProductBarcode('');
          }}
        />
      )}
    </>
  );
};

export default NutritionScreen;
